import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, LogOut, ChevronDown, ChevronUp, KeyRound, Trash2, Copy, Building2, Users, UserPlus, DollarSign, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const BILLING_STATUS_OPTIONS = [
  { value: "no_subscription", label: "Sin suscripción" },
  { value: "active", label: "Activa" },
  { value: "grace_period", label: "Periodo de gracia" },
  { value: "read_only", label: "Solo lectura" },
  { value: "canceled", label: "Cancelada" },
];

function BillingBadge({ status }) {
  const color =
    status === "active" ? "bg-green-100 text-green-800" :
    status === "grace_period" ? "bg-amber-100 text-amber-800" :
    status === "read_only" || status === "canceled" ? "bg-red-100 text-red-700" :
    "bg-charcoal/10 text-charcoal/70";
  const label = BILLING_STATUS_OPTIONS.find((o) => o.value === status)?.label || status || "—";
  return <span className={`text-xs font-semibold px-2 py-1 rounded-full ${color}`}>{label}</span>;
}

function AddAdminForm({ tenantId, onAdded }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [newPassword, setNewPassword] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/superadmin/companies/${tenantId}/admins`, { email, name });
      setNewPassword(data.new_password);
      toast.success(`Admin ${data.email} creado`);
      onAdded?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (newPassword) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
        <p className="text-charcoal mb-1">Admin creado. Contraseña temporal:</p>
        <div className="flex items-center gap-2">
          <span className="font-mono text-charcoal">{newPassword}</span>
          <button onClick={() => { navigator.clipboard?.writeText(newPassword); toast.success("Copiada"); }} className="text-petroleum hover:text-petroleum/70">
            <Copy className="w-3.5 h-3.5" />
          </button>
        </div>
        <button className="text-xs text-petroleum underline mt-2" onClick={() => { setOpen(false); setNewPassword(null); setEmail(""); setName(""); }}>Cerrar</button>
      </div>
    );
  }

  if (!open) {
    return (
      <Button type="button" variant="outline" size="sm" className="rounded-full h-8 text-xs" onClick={() => setOpen(true)}>
        <UserPlus className="w-3.5 h-3.5 mr-1" /> Agregar admin
      </Button>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white border border-petroleum/10 rounded-lg p-3 flex flex-wrap items-center gap-2">
      <input required type="text" placeholder="Nombre" value={name} onChange={(e) => setName(e.target.value)} className="text-sm border border-petroleum/20 rounded-full px-3 py-1.5 flex-1 min-w-[140px]" />
      <input required type="email" placeholder="correo@empresa.com" value={email} onChange={(e) => setEmail(e.target.value)} className="text-sm border border-petroleum/20 rounded-full px-3 py-1.5 flex-1 min-w-[180px]" />
      <Button type="submit" size="sm" disabled={saving} className="rounded-full h-8 text-xs bg-petroleum hover:bg-petroleum/90">Crear</Button>
      <button type="button" className="text-xs text-charcoal/50 underline" onClick={() => setOpen(false)}>Cancelar</button>
    </form>
  );
}

function CompanyUsers({ tenantId, onChanged }) {
  const [data, setData] = useState(null);
  const [revealed, setRevealed] = useState({});

  const load = () => {
    api.get(`/superadmin/companies/${tenantId}/users`).then(({ data }) => setData(data));
  };

  useEffect(load, [tenantId]);

  const resetPassword = async (userId, email) => {
    if (!window.confirm(`¿Generar una contraseña nueva para ${email}? La actual dejará de funcionar de inmediato.`)) return;
    try {
      const { data } = await api.post(`/superadmin/users/${userId}/reset-password`);
      setRevealed((prev) => ({ ...prev, [userId]: data.new_password }));
      toast.success(`Contraseña nueva generada para ${data.email}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const copyPassword = (password) => {
    navigator.clipboard?.writeText(password);
    toast.success("Contraseña copiada");
  };

  const deleteUser = async (userId, email, force = false) => {
    const confirmMsg = force
      ? `Es el único admin de esta empresa. ¿De verdad quieres borrar a ${email} de todos modos? La empresa se quedará sin nadie que pueda entrar.`
      : `¿Borrar la cuenta de ${email}? Esto no se puede deshacer.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await api.delete(`/superadmin/users/${userId}${force ? "?force=true" : ""}`);
      toast.success(`Usuario ${email} borrado`);
      load();
      onChanged?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = typeof detail === "string" ? detail : detail?.message;
      if (!force && message && message.includes("único admin")) {
        deleteUser(userId, email, true);
        return;
      }
      toast.error(formatApiErrorDetail(detail));
    }
  };

  if (!data) return <p className="text-sm text-charcoal/50 px-4 py-3">Cargando usuarios…</p>;

  return (
    <div className="bg-background/60 rounded-xl p-3 space-y-2">
      {data.users.length === 0 && <p className="text-sm text-charcoal/50 px-2">No hay usuarios en esta empresa.</p>}
      {data.users.map((u) => (
        <div key={u.id} className="bg-white border border-petroleum/10 rounded-lg p-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-sm font-medium text-charcoal">{u.name} <span className="text-charcoal/50 font-normal">— {u.email}</span></p>
            <p className="text-xs text-charcoal/50 mt-0.5">
              {u.role === "admin" ? "Administrador" : "Empleado"}
              {!u.email_verified && " · correo sin confirmar"}
              {u.must_change_password && " · debe cambiar contraseña"}
            </p>
            {revealed[u.id] && (
              <div className="mt-2 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                <span className="text-sm font-mono text-charcoal">{revealed[u.id]}</span>
                <button onClick={() => copyPassword(revealed[u.id])} className="text-petroleum hover:text-petroleum/70" title="Copiar">
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" className="rounded-full h-8 text-xs" onClick={() => resetPassword(u.id, u.email)}>
              <KeyRound className="w-3.5 h-3.5 mr-1" /> Resetear contraseña
            </Button>
            <Button type="button" variant="outline" size="sm" className="rounded-full h-8 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={() => deleteUser(u.id, u.email)}>
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Borrar
            </Button>
          </div>
        </div>
      ))}
      <AddAdminForm tenantId={tenantId} onAdded={load} />
    </div>
  );
}

export default function SuperAdminPanel() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [summary, setSummary] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [canceledCount, setCanceledCount] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [savingBilling, setSavingBilling] = useState(null);

  const load = () => {
    api.get("/superadmin/summary").then(({ data }) => setSummary(data));
    api.get("/superadmin/companies").then(({ data }) => setCompanies(data));
    api.get("/superadmin/revenue-summary").then(({ data }) => setRevenue(data));
    api.get("/superadmin/companies?billing_status=canceled").then(({ data }) => setCanceledCount(data.length));
  };

  useEffect(load, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const changeBillingStatus = async (tenantId, newStatus) => {
    setSavingBilling(tenantId);
    try {
      await api.post(`/superadmin/companies/${tenantId}/billing-status`, { billing_status: newStatus });
      toast.success("Estado de facturación actualizado");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingBilling(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-petroleum text-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-6 h-6" strokeWidth={1.5} />
          <span className="font-heading text-lg font-bold">CERH · Super Admin</span>
        </div>
        <Button type="button" variant="ghost" size="sm" className="text-white hover:bg-white/10" onClick={handleLogout}>
          <LogOut className="w-4 h-4 mr-1" /> Salir
        </Button>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {summary && (
          <div className="flex gap-4 mb-6">
            <div className="bg-card border border-petroleum/10 rounded-2xl px-6 py-4 flex items-center gap-3">
              <Building2 className="w-5 h-5 text-petroleum" />
              <div>
                <p className="text-2xl font-bold text-charcoal">{summary.total_companies}</p>
                <p className="text-xs text-charcoal/50">Empresas</p>
              </div>
            </div>
            <div className="bg-card border border-petroleum/10 rounded-2xl px-6 py-4 flex items-center gap-3">
              <Users className="w-5 h-5 text-petroleum" />
              <div>
                <p className="text-2xl font-bold text-charcoal">{summary.total_users}</p>
                <p className="text-xs text-charcoal/50">Usuarios totales</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate("/superadmin/ingresos")}
              className="text-left bg-card border border-petroleum/10 rounded-2xl px-6 py-4 flex items-center gap-3 hover:-translate-y-0.5 hover:shadow-lg transition-all"
            >
              <DollarSign className="w-5 h-5 text-emerald-600" />
              <div>
                <p className="text-2xl font-bold text-charcoal">
                  {revenue ? `$${revenue.total_monthly_revenue.toLocaleString("es-MX")}` : "–"}
                </p>
                <p className="text-xs text-charcoal/50">
                  Ingreso mensual{revenue?.contact_only_excluded > 0 ? ` (no incluye ${revenue.contact_only_excluded} plan(es) a medida)` : ""}
                </p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => navigate("/superadmin/canceladas")}
              className="text-left bg-card border border-petroleum/10 rounded-2xl px-6 py-4 flex items-center gap-3 hover:-translate-y-0.5 hover:shadow-lg transition-all"
            >
              <Ban className="w-5 h-5 text-red-600" />
              <div>
                <p className="text-2xl font-bold text-charcoal">{canceledCount ?? "–"}</p>
                <p className="text-xs text-charcoal/50">Empresas canceladas</p>
              </div>
            </button>
          </div>
        )}

        <h1 className="font-heading text-xl text-charcoal mb-4">Empresas</h1>

        <div className="space-y-3">
          {companies.map((c) => (
            <div key={c.id} className="bg-card border border-petroleum/10 rounded-2xl overflow-hidden">
              <div className="p-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-semibold text-charcoal flex items-center gap-2">
                    {c.company_name}
                    {c.is_sandbox && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-charcoal/10 text-charcoal/60">SANDBOX</span>}
                  </p>
                  <p className="text-xs text-charcoal/50 mt-0.5">{(Array.isArray(c.admin_emails) ? c.admin_emails.join(", ") : "") || "sin admin"}</p>
                  <p className="text-xs text-charcoal/50 mt-0.5">
                    {c.employee_count ?? "—"} empleado(s) · banda: {c.plan_tier || "—"}
                    {c.created_at && ` · registrada el ${new Date(c.created_at).toLocaleDateString("es-MX", { year: "numeric", month: "short", day: "numeric" })}`}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <BillingBadge status={c.billing_status} />
                  <select
                    className="text-xs border border-petroleum/20 rounded-full px-3 py-1.5 bg-white text-charcoal"
                    value={c.billing_status || ""}
                    disabled={savingBilling === c.id}
                    onChange={(e) => changeBillingStatus(c.id, e.target.value)}
                  >
                    {BILLING_STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-full h-8 text-xs"
                    onClick={() => setExpanded(expanded === c.id ? null : c.id)}
                  >
                    Usuarios {expanded === c.id ? <ChevronUp className="w-3.5 h-3.5 ml-1" /> : <ChevronDown className="w-3.5 h-3.5 ml-1" />}
                  </Button>
                </div>
              </div>
              {expanded === c.id && (
                <div className="px-4 pb-4">
                  <CompanyUsers tenantId={c.id} onChanged={load} />
                </div>
              )}
            </div>
          ))}
          {companies.length === 0 && <p className="text-sm text-charcoal/50">No hay empresas registradas todavía.</p>}
        </div>
      </div>
    </div>
  );
}
